import { useEffect, useState } from "react";
import { X, User as UserIcon, LogOut, Save, KeyRound, Sun, Moon, Palette, Bot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import { useTheme, type Theme } from "@/lib/theme";
import { UserInfo, updateProfile, changePassword, ApiError } from "@/lib/api";
import { fetchGuidePreference, setGuidePreference } from "@/lib/guide/api";

interface AccountPanelProps {
  user: UserInfo;
  /** D6: called after the persisted Guide ON/OFF preference was saved. */
  onGuidePreferenceChange?: () => void;
  onClose: () => void;
  onUserUpdated: (u: UserInfo) => void;
  onLogout: () => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Notice = { kind: "ok" | "err"; text: string } | null;

/** Personal cabinet: profile (name/username), appearance (theme),
 *  security (password change), logout. All backed by real API calls. */
export function AccountPanel({ user, onGuidePreferenceChange, onClose, onUserUpdated, onLogout }: AccountPanelProps) {
  const { theme, setTheme } = useTheme();

  // Profile
  const [name, setName] = useState(user.name ?? "");
  const [username, setUsername] = useState(user.username ?? "");
  const [saving, setSaving] = useState(false);
  const [profileNotice, setProfileNotice] = useState<Notice>(null);

  // Password
  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [passSaving, setPassSaving] = useState(false);
  const [passNotice, setPassNotice] = useState<Notice>(null);

  // D6: RealityAI Guide ON/OFF (D5 persisted preference realityai_guide_enabled).
  const [guidePref, setGuidePref] = useState<boolean | null>(null);
  const [guideBusy, setGuideBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchGuidePreference()
      .then((v) => alive && setGuidePref(v))
      .catch(() => alive && setGuidePref(null));
    return () => { alive = false; };
  }, []);

  const handleGuideToggle = async (next: boolean) => {
    setGuideBusy(true);
    try {
      const saved = await setGuidePreference(next);
      setGuidePref(saved);
      onGuidePreferenceChange?.();
    } finally {
      setGuideBusy(false);
    }
  };

  useEffect(() => {
    setName(user.name ?? "");
    setUsername(user.username ?? "");
  }, [user.id]);

  useEffect(() => {
    // clear sensitive fields on close
    setCurPass("");
    setNewPass("");
    setNewPass2("");
  }, [user.id]);

  const dirty = name !== (user.name ?? "") || username !== (user.username ?? "");

  const handleSave = async () => {
    setSaving(true);
    setProfileNotice(null);
    try {
      const updated = await updateProfile({
        name: name.trim() || undefined,
        username: username.trim() || undefined,
      });
      onUserUpdated(updated);
      setProfileNotice({ kind: "ok", text: "Сохранено." });
    } catch (e) {
      setProfileNotice({ kind: "err", text: e instanceof ApiError ? e.message : "Не удалось сохранить." });
    } finally {
      setSaving(false);
    }
  };

  const passDirty = curPass !== "" || newPass !== "" || newPass2 !== "";

  const handlePasswordSave = async () => {
    setPassNotice(null);
    if (newPass.length < 6) {
      setPassNotice({ kind: "err", text: "Новый пароль: минимум 6 символов." });
      return;
    }
    if (newPass !== newPass2) {
      setPassNotice({ kind: "err", text: "Пароли не совпадают." });
      return;
    }
    setPassSaving(true);
    try {
      await changePassword(curPass, newPass);
      setCurPass("");
      setNewPass("");
      setNewPass2("");
      setPassNotice({ kind: "ok", text: "Пароль изменён. Используйте его при следующем входе." });
    } catch (e) {
      setPassNotice({ kind: "err", text: e instanceof ApiError ? e.message : "Не удалось изменить пароль." });
    } finally {
      setPassSaving(false);
    }
  };

  const inputCls =
    "w-full h-10 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent transition-colors";
  const labelCls = "block text-xs text-tertiary mb-1";
  const sectionTitle = "text-xs font-medium text-tertiary uppercase tracking-wider";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center md:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative flex flex-col w-full h-full md:max-w-2xl md:max-h-[85vh] md:h-auto md:rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
          <h2 className="text-lg font-medium text-primary flex items-center gap-2">
            <UserIcon size={18} className="text-accent" /> Настройки
          </h2>
          <Tip label="Закрыть" side="bottom">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-7">
          {/* 1. Read-only account info */}
          <section className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-tertiary">Email</span>
              <span className="text-sm text-primary truncate max-w-[180px]">{user.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-tertiary">Роль</span>
              <span className="text-sm text-primary">{user.role ?? "USER"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-tertiary">Баланс</span>
              <span className="text-sm text-primary">{user.balance ?? 0} монет</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-tertiary">Тариф</span>
              <span className="text-sm text-primary">{user.tariff_code || "Free"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-tertiary">Последний вход</span>
              <span className="text-sm text-primary">{fmtDate(user.last_login_at)}</span>
            </div>
          </section>

          {/* 2. Profile (self-service edit) */}
          <section className="space-y-3">
            <h3 className={sectionTitle}>Профиль</h3>
            <div>
              <label className={labelCls}>Имя</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Имя пользователя</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} />
              <p className="text-[11px] text-tertiary/80 mt-1">
                Только буквы и цифры (3–50 симв.).
              </p>
            </div>
            {profileNotice && (
              <div
                className={cn(
                  "text-xs px-3 py-2 rounded-lg",
                  profileNotice.kind === "ok"
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-red-500/10 text-red-400"
                )}
              >
                {profileNotice.text}
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className={cn(
                "w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                !dirty || saving
                  ? "bg-hoverbg text-disabled cursor-not-allowed"
                  : "bg-accent text-accent-contrast hover:opacity-90"
              )}
            >
              <Save size={15} />
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          </section>

          {/* 3. Appearance: two first-class themes (dark primary / light) */}
          <section className="space-y-3">
            <h3 className={sectionTitle}>Внешний вид</h3>
            <div className="grid grid-cols-2 gap-2">
              <ThemeCard
                active={theme === "dark"}
                onClick={() => setTheme("dark")}
                icon={<Moon size={16} />}
                label="Тёмная"
                sub="Основная"
                swatch="bg-[#191a1a] border-white/20"
              />
              <ThemeCard
                active={theme === "light"}
                onClick={() => setTheme("light")}
                icon={<Sun size={16} />}
                label="Светлая"
                sub="Вторичная"
                swatch="bg-white border-black/20"
              />
            </div>
          </section>

          {/* 3b. D6: RealityAI Guide ON/OFF (D5 persisted preference) */}
          <section className="space-y-3">
            <h3 className={sectionTitle}>RealityAI Guide</h3>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-hairline bg-hoverbg/40 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                  <Bot size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-primary">Помощник RealityAI</p>
                  <p className="text-[11px] text-tertiary">
                    Робот-ассистент RealityAI: отвечает на вопросы и помогает находить
                    информацию в документации.
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={guidePref ?? true}
                aria-label="RealityAI Guide"
                disabled={guideBusy || guidePref === null}
                onClick={() => handleGuideToggle(!(guidePref ?? true))}
                className={cn(
                  "relative w-11 h-6 rounded-full transition-colors shrink-0",
                  (guidePref ?? true) ? "bg-accent" : "bg-hoverbg2 border border-hairline",
                  (guideBusy || guidePref === null) && "opacity-60"
                )}
              >
                <span
                  className={cn(
                    "absolute left-0 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
                    (guidePref ?? true) ? "translate-x-[22px]" : "translate-x-0.5"
                  )}
                />
              </button>
            </div>
            <p className="text-[11px] text-tertiary/80">
              При выключении помощник скрывается, но документация остаётся доступной.
            </p>
          </section>

          {/* 4. Security: password change (current required) */}
          <section className="space-y-3">
            <h3 className={sectionTitle}>Безопасность</h3>
            <div>
              <label className={labelCls}>Текущий пароль</label>
              <input
                type="password"
                value={curPass}
                onChange={(e) => setCurPass(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Новый пароль</label>
              <input
                type="password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder="Минимум 6 символов"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Повторите новый пароль</label>
              <input
                type="password"
                value={newPass2}
                onChange={(e) => setNewPass2(e.target.value)}
                placeholder="Ещё раз"
                className={inputCls}
              />
            </div>
            {passNotice && (
              <div
                className={cn(
                  "text-xs px-3 py-2 rounded-lg",
                  passNotice.kind === "ok"
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-red-500/10 text-red-400"
                )}
              >
                {passNotice.text}
              </div>
            )}
            <button
              onClick={handlePasswordSave}
              disabled={!passDirty || passSaving}
              className={cn(
                "w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                !passDirty || passSaving
                  ? "bg-hoverbg text-disabled cursor-not-allowed"
                  : "bg-accent text-accent-contrast hover:opacity-90"
              )}
            >
              <KeyRound size={15} />
              {passSaving ? "Смена..." : "Сменить пароль"}
            </button>
          </section>

          {/* 5. Logout */}
          <section className="pt-1">
            <button
              onClick={onLogout}
              className="w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <LogOut size={15} /> Выйти
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

function ThemeCard({
  active,
  onClick,
  icon,
  label,
  sub,
  swatch,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  sub: string;
  swatch: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-xl border p-3 flex flex-col items-start gap-2 transition-colors text-left",
        active
          ? "border-accent bg-accent/10"
          : "border-hairline bg-hoverbg hover:bg-hoverbg2"
      )}
    >
      <span className={cn("w-8 h-8 rounded-md border flex items-center justify-center", swatch, "text-primary")}>
        {icon}
      </span>
      <span className="text-sm font-medium text-primary flex items-center gap-1.5">
        <Palette size={12} className="text-accent" /> {label}
      </span>
      <span className="text-[11px] text-tertiary">{sub}</span>
    </button>
  );
}